import api from "./api";
import { can } from "./permissions";
import { permissionFor } from "./routePermissions";

/*
 * Where a stored notification leads. The backend sends a logical target
 * (`{ type, id, params }`), never a URL; this is the one place the portal maps
 * it to a route. Opening only navigates — it never performs an action.
 *
 * Each type names its detail route, the route-permission pattern guarding it,
 * an optional existence check, and the list page to fall back to when the
 * target is gone or not allowed.
 */
const TARGETS = {
  appointment: {
    path: ({ id }) => `/appointments/${id}`,
    pattern: "/appointments/:id",
    exists: ({ id }) => `/business/appointments/${id}`,
    fallback: "/diary",
  },
  review: {
    path: ({ id, branchId }) =>
      `/reviews?${new URLSearchParams({ ...(branchId ? { branch: branchId } : {}), review: id })}`,
    pattern: "/reviews",
    fallback: "/reviews",
  },
  branch: {
    path: ({ id }) => `/branches/${id}`,
    pattern: "/branches/:id",
    exists: ({ id }) => `/business/branches/${id}`,
    fallback: "/branches",
  },
  // A product at one branch (low / out of stock): that branch's products,
  // with the product's stock dialog open. Ids ride in `params`.
  branch_product: {
    path: ({ params }) =>
      params.branch_id && params.product_id
        ? `/branches/${params.branch_id}/products?adjust=${params.product_id}`
        : "/products",
    pattern: "/branches/:id/products",
    exists: ({ params }) =>
      params.branch_id && params.product_id
        ? `/business/branches/${params.branch_id}/products/${params.product_id}`
        : null,
    fallback: "/products",
  },
  // A queued report export is ready (RPT-01): the Reports page downloads it
  // from `?export=`. Only the requester ever gets this notification.
  report_export: {
    path: ({ id }) => `/reports?export=${id}`,
    pattern: "/reports",
    fallback: "/reports",
  },
  staff_schedule: {
    path: ({ id }) => `/providers/${id}?tab=schedule`,
    pattern: "/providers/:id",
    exists: ({ id }) => `/business/staff/${id}`,
    fallback: "/providers",
  },
};

export const isKnownTarget = (target) => Boolean(target?.type && TARGETS[target.type]);

const allowed = (user, pattern) => {
  const permission = permissionFor(pattern);
  return !permission || can(user, permission);
};

/**
 * Resolve a target to `{ path, missing }`. `missing` means the user lands on a
 * fallback (gone, or not theirs to see) and should be told so. Unknown types
 * resolve to null: the caller only marks the item read.
 */
export async function resolveNotificationTarget(target, { user, branchId } = {}) {
  // Spec 08: a notification about something that is also a managed alert
  // (low / out of stock, a low rating) opens that alert.
  if (target?.params?.alert_id && can(user, "alerts.view")) {
    return { path: `/alerts?alert=${target.params.alert_id}`, missing: false };
  }

  const spec = target?.type ? TARGETS[target.type] : null;
  if (!spec || !target.id) return null;

  const fallback = allowed(user, spec.fallback) ? spec.fallback : "/";
  const args = { id: target.id, params: target.params ?? {}, branchId };

  if (!allowed(user, spec.pattern)) return { path: fallback, missing: true };

  const existsUrl = spec.exists ? spec.exists(args) : null;

  if (spec.exists && !existsUrl) return { path: fallback, missing: true };

  if (existsUrl) {
    try {
      await api.get(existsUrl);
    } catch (err) {
      const status = err.response?.status;
      // Gone or not theirs. Anything else (offline, 5xx) still opens the page,
      // which has its own error handling.
      if (status === 403 || status === 404) return { path: fallback, missing: true };
    }
  }

  return { path: spec.path(args), missing: false };
}
